import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Input,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { finalize } from 'rxjs';
import { resolveDateWindow } from '../../core/report-builders';
import { ReportsApiService } from '../../core/reports-api.service';
import { ReportsCsvExportService } from '../../core/reports-csv-export.service';
import {
  DatePreset,
  ReportResult,
  ReportTableRow,
  ReportType,
  TableColumn,
  formatReportCellValue,
} from '../../core/report-types';

@Component({
  selector: 'app-report-runner',
  imports: [CommonModule, FormsModule],
  templateUrl: './report-runner.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReportRunnerComponent implements OnInit {
  @Input({ required: true }) reportType!: ReportType;
  @Input({ required: true }) title = '';
  @Input({ required: true }) description = '';

  private readonly reportsApiService = inject(ReportsApiService);
  private readonly csvExportService = inject(ReportsCsvExportService);
  private readonly destroyRef = inject(DestroyRef);

  readonly loading = signal(false);
  readonly errorMessage = signal<string | null>(null);
  readonly validationMessage = signal<string | null>(null);
  readonly reportResult = signal<ReportResult | null>(null);
  readonly csvDownloadUrl = signal<string | null>(null);
  readonly csvFileName = signal('report.csv');

  readonly datePresetOptions: Array<{ value: DatePreset; label: string }> = [
    { value: 'previous_month', label: 'Previous Month' },
    { value: 'previous_quarter', label: 'Previous Quarter' },
    { value: 'custom', label: 'Custom Range' },
  ];

  readonly filters = signal({
    datePreset: 'previous_month' as DatePreset,
    startDate: '',
    endDate: '',
  });

  readonly canDownloadCsv = computed(
    () => !!this.csvDownloadUrl() && (this.reportResult()?.rows.length ?? 0) > 0,
  );

  readonly activeFilterSummary = computed(() => {
    const current = this.filters();
    const period = resolveDateWindow({
      reportType: this.reportType,
      datePreset: current.datePreset,
      startDate: current.startDate,
      endDate: current.endDate,
      status: 'all',
      partnerOrganizationId: null,
      serviceName: null,
    });

    return `Program Period: ${period.start} to ${period.end}`;
  });

  ngOnInit(): void {
    this.runReport();

    this.destroyRef.onDestroy(() => {
      this.revokeCsvUrl();
    });
  }

  setDatePreset(value: unknown): void {
    if (value !== 'previous_month' && value !== 'previous_quarter' && value !== 'custom') {
      return;
    }

    const next = { ...this.filters(), datePreset: value as DatePreset };
    if (value !== 'custom') {
      next.startDate = '';
      next.endDate = '';
    }

    this.filters.set(next);
    this.validationMessage.set(null);
  }

  setCustomDateStart(value: unknown): void {
    if (typeof value !== 'string') {
      return;
    }

    this.filters.set({ ...this.filters(), startDate: value });
  }

  setCustomDateEnd(value: unknown): void {
    if (typeof value !== 'string') {
      return;
    }

    this.filters.set({ ...this.filters(), endDate: value });
  }

  runReport(): void {
    this.validationMessage.set(null);
    this.errorMessage.set(null);

    const current = this.filters();
    if (current.datePreset === 'custom') {
      if (!current.startDate || !current.endDate) {
        this.validationMessage.set('Custom date range requires both start and end dates.');
        return;
      }

      if (current.startDate > current.endDate) {
        this.validationMessage.set('Start date cannot be after end date.');
        return;
      }
    }

    this.loading.set(true);
    this.reportsApiService
      .runReport(this.reportType, {
        datePreset: current.datePreset,
        startDate: current.startDate,
        endDate: current.endDate,
      })
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (result) => {
          this.reportResult.set(result);
          this.updateCsv(result);
        },
        error: (error) => {
          this.errorMessage.set(
            error?.error?.message ?? 'Unable to run report right now. Please try again.',
          );
          this.reportResult.set(null);
          this.revokeCsvUrl();
        },
      });
  }

  formatCellValue(value: unknown): string {
    if (typeof value !== 'string' && typeof value !== 'number') {
      return '';
    }

    return formatReportCellValue(value);
  }

  trackColumn(_index: number, column: TableColumn): string {
    return column.key;
  }

  trackRow(index: number, row: ReportTableRow): string {
    return String(row['id'] ?? row['metric'] ?? row['category'] ?? row['value'] ?? index);
  }

  private updateCsv(result: ReportResult): void {
    this.revokeCsvUrl();

    const download = this.csvExportService.createDownload(result, this.reportType);
    this.csvDownloadUrl.set(download.url);
    this.csvFileName.set(download.fileName);
  }

  private revokeCsvUrl(): void {
    this.csvExportService.revoke(this.csvDownloadUrl());
    this.csvDownloadUrl.set(null);
  }
}
